/**
 * Options research: where premium has been rich, and by how much.
 *
 * Not a buy list, deliberately. The question "which options should I buy" has
 * no honest answer from this data; what the data can answer is whether implied
 * volatility has historically exceeded the volatility that followed, how often,
 * and over how many independent windows -- which is the one options effect this
 * platform can measure out of sample.
 *
 * Symbols without a free volatility index cannot be measured at all yet. They
 * are shown as accumulating, with their own progress, rather than filled in
 * with an estimate from nothing.
 */
import { useEffect } from "react";

import { useAsync } from "../hooks/useApi";
import { api } from "../lib/api";

const pct = (v: number | null | undefined, dp = 1) =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(dp)}%`;
const pts = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}`;

export function OptionsResearch({ onClose }: { onClose: () => void }) {
  const res = useAsync((s) => api.optionsResearch(s), []);
  const screen = useAsync((s) => api.newsScreen(s), []);
  const market = useAsync((s) => api.marketScan(false, s), []);

  // While the page is open, keep it current. Five minutes rather than the
  // engine's ten-minute cache, so a refreshed scan is picked up promptly
  // without the request ever doing the work itself -- a warm cache answers
  // immediately and a cold one is rebuilt by the scheduler, not by this.
  useEffect(() => {
    const t = setInterval(() => {
      market.refetch();
      screen.refetch();
    }, 5 * 60_000);
    return () => clearInterval(t);
    // The refetchers are stable; listing them would restart the timer on
    // every response, which is the one thing this must not do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const d = res.data;
  const sc = screen.data;

  return (
    <div className="admin">
      <div className="admin-head">
        <h2>Options research</h2>
        <span className="spacer" />
        <button className="btn" onClick={() => { res.refetch(); screen.refetch(); market.refetch(); }}>
          Refresh
        </button>
        <button className="btn" onClick={onClose}>Close</button>
      </div>

      {res.loading && <div className="hint"><span className="spinner" /> measuring…</div>}
      {res.error && <div className="admin-note">{res.error.message}</div>}

      {d && (
        <>
          <div className="hint" style={{ marginBottom: 12 }}>
            Variance risk premium over a {d.horizon_days}-day horizon: how often
            implied volatility exceeded the volatility that actually followed.
          </div>

          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th style={{ textAlign: "right" }}>Windows</th>
                  <th style={{ textAlign: "right" }}>IV &gt; RV</th>
                  <th style={{ textAlign: "right" }}>Rate</th>
                  <th style={{ textAlign: "right" }}>95% interval</th>
                  <th style={{ textAlign: "right" }}>Mean premium</th>
                  <th>What it favours</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.symbol}>
                    <td className="mono" style={{ fontWeight: 600 }}>{r.symbol}</td>
                    {r.error ? (
                      <td colSpan={6} className="faint small">{r.error}</td>
                    ) : (
                      <>
                        <td className="num">{r.observations}</td>
                        <td className="num">{r.implied_exceeded_realised}</td>
                        <td className="num up">{pct(r.hit_rate)}</td>
                        {/* The interval, not just the point estimate: a rate
                            whose interval straddles a half says nothing. */}
                        <td className="num small faint">
                          {pct(r.ci_low)} – {pct(r.ci_high)}
                        </td>
                        <td className={`num ${(r.mean_premium ?? 0) > 0 ? "up" : "down"}`}>
                          {pts(r.mean_premium)} pts
                        </td>
                        <td className="small">
                          {r.direction
                            ? <span className="badge good">{r.direction} premium</span>
                            : <span className="faint">{r.reading}</span>}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.pending.symbols.length > 0 && (
            <>
              <h3>Accumulating</h3>
              <div className="hint" style={{ marginBottom: 8 }}>
                These have no free volatility index, so their own at-the-money
                implied vol is recorded daily. Roughly 260 trading days are
                needed before the same measurement can be made.
              </div>
              <div className="kv">
                {d.pending.symbols.slice(0, 12).map((s) => (
                  <div className="kv-cell" key={s.symbol}>
                    <div className="k">{s.symbol}</div>
                    <div className="v">{s.days}<span className="faint" style={{ fontSize: 13 }}> / {s.ready_at}</span></div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="notebox" style={{ marginTop: 14 }}>{d.caveat}</div>
        </>
      )}

      {/* The widest question: the whole market, not a watchlist someone has
          already written. */}
      <h3>Across the US market</h3>
      {market.loading && (
        <div className="hint"><span className="spinner" /> scanning every liquid US stock…</div>
      )}
      {market.data?.error && <div className="admin-note">{market.data.error}</div>}
      {market.data && market.data.rows.length > 0 && (
        <>
          <div className="hint" style={{ marginBottom: 8 }}>
            {market.data.scanned?.toLocaleString()} symbols →{" "}
            {market.data.liquid?.toLocaleString()} liquid →{" "}
            {market.data.measured?.toLocaleString()} measured, in{" "}
            {((market.data.took_ms ?? 0) / 1000).toFixed(1)}s. Ranked by how far
            implied volatility sits below what the stock has actually been doing.
          </div>
          <div className="table-scroll" style={{ maxHeight: "46vh", overflowY: "auto" }}>
            <table className="grid chain-list">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Last</th>
                  <th>Realised 20d</th>
                  <th>Vol trend</th>
                  <th>Chain IV</th>
                  <th>IV − realised</th>
                  <th>Strikes</th>
                </tr>
              </thead>
              <tbody>
                {market.data.rows.slice(0, 40).map((r) => (
                  <tr key={r.symbol}>
                    <td className="mono" style={{ fontWeight: 600 }}>{r.symbol}</td>
                    <td className="num">{r.last.toFixed(2)}</td>
                    <td className="num">{pct(r.realised_20d)}</td>
                    <td className="num up">{r.expansion.toFixed(2)}×</td>
                    <td className="num">{pct(r.chain_iv)}</td>
                    <td className="num up">{pts(r.gap)} pts</td>
                    {/* The count is here because the gap is a median over the
                        chain: on a thin one it rests on very little. */}
                    <td className={`num ${r.strikes < 40 ? "down" : "faint"}`}>{r.strikes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* What the screen's own flags have done. Recorded on every scan
              and scored 28 days later, so this fills in by itself -- and says
              plainly when there is not yet enough of it to mean anything. */}
          {market.data.track_record && (
            <div className="notebox" style={{ marginTop: 12 }}>
              <b>This screen's own record.</b>{" "}
              {market.data.track_record.resolved === 0 ? (
                <>
                  {market.data.track_record.flagged} flags recorded
                  {market.data.track_record.since && ` since ${market.data.track_record.since}`};
                  none has reached its 28-day window yet. Once they do, this
                  line reports whether each gap closed because implied
                  volatility rose to meet the movement, or because the stock
                  calmed down instead — which are opposite outcomes for anyone
                  who bought premium on the flag.
                </>
              ) : (
                <>
                  {market.data.track_record.resolved} of{" "}
                  {market.data.track_record.flagged} flags resolved:{" "}
                  <span className="up">{market.data.track_record.iv_rose} repriced upward</span>,{" "}
                  <span className="down">{market.data.track_record.realised_fell} calmed down</span>,{" "}
                  {market.data.track_record.still_open} still open.
                  {!market.data.track_record.enough_to_judge &&
                    " Too few resolved to judge the screen by — 30 is the minimum this app treats as a sample."}
                </>
              )}
            </div>
          )}
          {market.data.caveat && (
            <div className="notebox" style={{ marginTop: 12 }}>{market.data.caveat}</div>
          )}
        </>
      )}

      {/* The second question: where is something happening that the option
          market may not have met yet. Ranked by implied against recent
          realised movement -- an ordering to look at, not a verdict. */}
      <h3>News and movement</h3>
      {screen.loading && <div className="hint"><span className="spinner" /> screening…</div>}
      {sc && sc.rows.length > 0 && (
        <>
          <div className="hint" style={{ marginBottom: 8 }}>
            Your watchlist over {sc.days ?? 7} days, ordered by where implied
            volatility sits furthest below what the stock has actually been
            doing.
          </div>
          <div className="table-scroll">
            <table className="grid">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th style={{ textAlign: "right" }}>Stories</th>
                  <th style={{ textAlign: "right" }}>Tone</th>
                  <th style={{ textAlign: "right" }}>Realised 20d</th>
                  <th style={{ textAlign: "right" }}>Vol trend</th>
                  <th style={{ textAlign: "right" }}>Rel. volume</th>
                  <th style={{ textAlign: "right" }}>ATM IV</th>
                  <th style={{ textAlign: "right" }}>IV − realised</th>
                </tr>
              </thead>
              <tbody>
                {sc.rows.map((r) => (
                  <tr key={r.symbol}>
                    <td className="mono" style={{ fontWeight: 600 }}>{r.symbol}</td>
                    <td className={`num ${r.articles ? "" : "faint"}`}>{r.articles}</td>
                    <td className={`num ${(r.sentiment ?? 0) > 0.05 ? "up" : (r.sentiment ?? 0) < -0.05 ? "down" : "faint"}`}>
                      {r.sentiment === null ? "—" : `${r.sentiment >= 0 ? "+" : ""}${r.sentiment.toFixed(2)}`}
                    </td>
                    <td className="num">{pct(r.realised_vol_20d)}</td>
                    <td className={`num ${(r.vol_expanding ?? 1) > 1 ? "up" : "faint"}`}>
                      {r.vol_expanding ? `${r.vol_expanding.toFixed(2)}×` : "—"}
                    </td>
                    <td className="num faint">
                      {r.relative_volume ? `${r.relative_volume.toFixed(2)}×` : "—"}
                    </td>
                    <td className="num">{pct(r.atm_iv)}</td>
                    {/* Negative is the interesting end: the option market has
                        not moved to meet the movement already happening. */}
                    <td className={`num ${(r.iv_less_rv ?? 0) < 0 ? "up" : "down"}`}>
                      {r.iv_less_rv === null ? "—" : `${pts(r.iv_less_rv)} pts`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {sc.caveat && <div className="notebox" style={{ marginTop: 12 }}>{sc.caveat}</div>}
        </>
      )}
    </div>
  );
}
